/**
 * Loyalty store sign-up form schema
 * Based on existing loyalty-store-sign-up module
 */

import { z } from 'zod';
import {
    emailSchema,
    phoneSchema,
    optionalBirthdaySchema,
    birthdaySchema,
    optionalNameSchema,
    nameSchema,
    passwordSchema,
    optionalCheckboxSchema
} from '../base-fields';

/**
 * Base loyalty signup schema (email + phone only)
 */
export const baseLoyaltySignupSchema = z.object({
    email: emailSchema,
    phone: phoneSchema,
    genMarketing: optionalCheckboxSchema,
    profiling: optionalCheckboxSchema,
    thirdParty: optionalCheckboxSchema
});

/**
 * Extended loyalty signup schema with account creation fields
 */
export const extendedLoyaltySignupSchema = baseLoyaltySignupSchema
    .extend({
        firstName: optionalNameSchema,
        lastName: optionalNameSchema,
        password: passwordSchema,
        confirmPassword: passwordSchema,
        birthday: optionalBirthdaySchema,
        postalCode: z.string().optional()
    })
    .refine(data => !data.password || data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword']
    });

/**
 * Get loyalty signup schema based on locale
 */
export const getLoyaltySignupSchema = (locale?: string) => {
    switch (locale?.toLowerCase()) {
        case 'enus':
            // US requires phone, name, lastname mandatory
            return baseLoyaltySignupSchema.extend({
                firstName: nameSchema,
                lastName: nameSchema,
                phone: phoneSchema
            });

        case 'enau':
            // AU requires phone, birthday mandatory; name, lastname optional
            return baseLoyaltySignupSchema.extend({
                phone: phoneSchema,
                birthday: birthdaySchema,
                firstName: optionalNameSchema,
                lastName: optionalNameSchema
            });

        default:
            // Base schema: email + phone + consents
            return baseLoyaltySignupSchema;
    }
};

// Type inference
export type LoyaltySignupFormData = z.infer<typeof baseLoyaltySignupSchema>;
export type ExtendedLoyaltySignupFormData = z.infer<typeof extendedLoyaltySignupSchema>;
